'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function BotoksSonrasiAlkolTuketimi() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/botoks-sonrasi-alkol-tuketimi-bilimsel-aciklamalar-klinik-etkiler-ve-uzman-onerileri';
  const pageTitle = 'Botoks Sonrası Alkol Tüketimi: Bilimsel Açıklamalar, Klinik Etkiler ve Uzman Önerileri';
  const pageDescription = 'Botoks sonrası alkol içmek neden önerilmez? Dolaşım, kas aktivitesi ve ödem üzerindeki biyolojik etkiler ile bilimsel bekleme süreleri.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Botoks Sonrası Alkol Tüketimi', href: '/tr/blog/botoks-sonrasi-alkol-tuketimi-bilimsel-aciklamalar-klinik-etkiler-ve-uzman-onerileri' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Botoks sonrası alkol tüketimi ve etkileri"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-red-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Botoks Bakımı
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Botoks Sonrası Alkol Tüketimi: Bilimsel Açıklamalar, Klinik Etkiler ve Uzman Önerileri
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>28 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>8 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Botoks (Botulinum Toksini), estetik tıp alanında kas aktivitesini kontrollü şekilde azaltarak kırışıklıkları yumuşatan en popüler enjeksiyon uygulamasıdır.
                İşlemden sonra gösterilecek bakım ve davranışlar ise sonucun kalıcılığını belirler.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Hastaların en sık sorduğu sorulardan biri:
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <p className="text-red-800 font-medium text-lg">
                  "Botoks yaptırdıktan sonra alkol alabilir miyim?"
                </p>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu soru, yalnızca estetik bir detay değil, aynı zamanda biyolojik bir dengedir.
                Çünkü alkol, damar sistemi, kas fonksiyonu ve metabolizma üzerinde etkili bir maddedir — ve bunların hepsi botoksun etki mekanizmasıyla doğrudan ilişkilidir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣ Botoks Nasıl Çalışır?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks, sinir uçlarında bulunan asetilkolin adlı ileti maddesinin salınımını geçici olarak durdurur.
                Böylece kas gevşer ve üstteki cilt yüzeyi gerilir.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <p className="text-blue-800 mb-4">
                  Etkisinin yerel kalması gerektiği için, enjeksiyon sonrası ilk 48 saatte kas içindeki toksinin sabitlenmesi çok önemlidir.
                </p>
                <p className="text-blue-800 font-medium">
                  İşte tam bu süre içinde alkol devreye girdiğinde fizyoloji bozulur.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2️⃣ Alkolün Vücut Üzerindeki Fizyolojik Etkileri
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-red-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Etki</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Damar genişlemesi (vazodilatasyon)</td>
                      <td className="border border-gray-300 px-4 py-3">Kılcal damar çapını artırır, enjeksiyon bölgesine fazla kan akışı sağlar.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kan incelmesi</td>
                      <td className="border border-gray-300 px-4 py-3">Pıhtılaşma süresini uzatır, morarma riskini yükseltir.</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sıvı tutulumu</td>
                      <td className="border border-gray-300 px-4 py-3">Yüz bölgesinde ödem ve şişlik oluşturur.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kas gevşemesi</td>
                      <td className="border border-gray-300 px-4 py-3">Botoksun doz etkisini dengeleyemez hale getirir.</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Karaciğer yükü artışı</td>
                      <td className="border border-gray-300 px-4 py-3">Toksin metabolizması gecikir, etki geç oturur.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                <p className="text-orange-800 font-medium">
                  📍 Sonuç: Alkol, botoksun hedef kas içinde sabit kalmasını bozabilir ve sonucun asimetrik olma riskini artırır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3️⃣ Neden 48 Saat Beklenmeli?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                İşlemden sonra botulinum molekülünün kas içinde yerleşme ve reseptörlere bağlanma süreci yaklaşık 24–48 saat sürer.
              </p>

              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <p className="text-green-800 mb-4 font-medium">
                  Bu süre içinde:
                </p>
                <ul className="list-disc list-inside space-y-2 text-green-800">
                  <li>Damar basıncı artmamalı,</li>
                  <li>Metabolik ısınma olmamalı,</li>
                  <li>Kaslara fazla kan gitmemelidir.</li>
                </ul>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Alkol aldığınızda vücut ısısı ve kan akımı artar → botoks daha geniş bir alana yayılabilir.
                Bu da "kas asimetresi", "kaş dengesizliği" veya "göz kapağı düşüklüğü" gibi komplikasyonlara zemin hazırlar.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4️⃣ Alkol Türlerine Göre Etkiler
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-purple-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Alkol Türü</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Etki Düzeyi</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Not</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Şarap / Bira</td>
                      <td className="border border-gray-300 px-4 py-3">Orta</td>
                      <td className="border border-gray-300 px-4 py-3">Damar genişletici etki orta seviyededir.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Distile içecekler (vodka, viski)</td>
                      <td className="border border-gray-300 px-4 py-3">Yüksek</td>
                      <td className="border border-gray-300 px-4 py-3">Kan basıncında ani artış yapar.</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kokteyller</td>
                      <td className="border border-gray-300 px-4 py-3">Yüksek + şekerli etki</td>
                      <td className="border border-gray-300 px-4 py-3">Şeker ödem riskini katlar.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Az alkollü içecekler</td>
                      <td className="border border-gray-300 px-4 py-3">Düşük</td>
                      <td className="border border-gray-300 px-4 py-3">Yine de tavsiye edilmez.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5️⃣ Estetik Sonuca Etkisi
              </h2>
              
              <div className="bg-yellow-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-yellow-800">
                  <li>Alkol sonrası oluşan şişlik ve kızarıklık, botoks sonucunu gizleyebilir.</li>
                  <li>Bazı hastalarda gerçek etki 7 gün yerine 10–12 günde ortaya çıkar.</li>
                  <li>Bazı durumlarda ise botoks dengesiz dağılır ve "tek taraflı kaş kalkması" gibi geçici asimetri oluşabilir.</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6️⃣ Klinik Gözlemlerden Örnek
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Bir hastam işlemden 6 saat sonra şarap içtiğini söylemişti. Ertesi gün hafif bir kaş asimetrisi gelişti ve ek rötuş gerekti.
                  Aynı işlem aynı kişide 48 saat beklediğinde mükemmel sonuç verdi."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7️⃣ Botoks Sonrası Alkol İçmenin Sonuçları
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-red-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Sonuç</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Süre</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Morarma (Ekimoz)</td>
                      <td className="border border-gray-300 px-4 py-3">Kılcal damar sızıntısı → 2–5 gün sürebilir</td>
                      <td className="border border-gray-300 px-4 py-3">Geçici</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Ödem / Şişlik</td>
                      <td className="border border-gray-300 px-4 py-3">Sıvı tutulumu → 1–3 gün</td>
                      <td className="border border-gray-300 px-4 py-3">Buz uygulamasıyla azalır</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Asimetri</td>
                      <td className="border border-gray-300 px-4 py-3">Toksin yayılımı → 1–2 haftada dengeye gelir</td>
                      <td className="border border-gray-300 px-4 py-3">Rötuş gerekebilir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Etkide zayıflama</td>
                      <td className="border border-gray-300 px-4 py-3">Reseptör dağılımı bozulabilir</td>
                      <td className="border border-gray-300 px-4 py-3">Kalıcı değil ama etki kısalır</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8️⃣ Etik ve Klinik Açıdan Bakış
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Estetik işlemler, hastanın vücut fizyolojisine müdahaledir; dolayısıyla geçici de olsa bir "iyileşme dönemi" vardır.
                Hastanın bu dönemde uygulamayı koruması, hem hekimin emeğine hem de kendi yatırımına saygıdır.
              </p>

              <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 mb-6">
                <p className="text-indigo-800 font-medium">
                  💬 "Estetikte sabır, sonucun yarısıdır."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                9️⃣ Botoks Sonrası Alkol Yerine Ne Yapılabilir?
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-green-50 rounded-xl p-6">
                  <h4 className="font-semibold text-green-900 mb-4 flex items-center">
                    <span className="text-green-600 mr-2">✅</span>
                    Alternatif
                  </h4>
                  <ul className="space-y-2 text-green-800">
                    <li>• Bol su içmek</li>
                    <li>• Bitki çayı (adaçayı, melisa)</li>
                    <li>• Antioksidan gıdalar (yaban mersini, C vit.)</li>
                    <li>• Soğuk kompres</li>
                  </ul>
                </div>
                
                <div className="bg-blue-50 rounded-xl p-6">
                  <h4 className="font-semibold text-blue-900 mb-4 flex items-center">
                    <span className="text-blue-600 mr-2">💡</span>
                    Etki
                  </h4>
                  <ul className="space-y-2 text-blue-800">
                    <li>• Toksin metabolizmasını düzenler</li>
                    <li>• Dolaşımı dengeleyici</li>
                    <li>• Hücre yenilenmesini destekler</li>
                    <li>• Morarma ve ödem riskini azaltır</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                🔟 Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Botokstan sonra bir kadeh şarap içtim, ne olur?</h3>
                  <p className="text-gray-600">Muhtemelen geçici ödem oluşur. Bol su ve soğuk kompres uygulayın.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Botokstan sonra kaç gün alkol yasak?</h3>
                  <p className="text-gray-600">En az 48 saat, tercihen 72 saat.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Alkol botoksun etkisini yok eder mi?</h3>
                  <p className="text-gray-600">Tamamen değil ama etkinin homojenliğini azaltabilir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Botokstan sonra sigara içilebilir mi?</h3>
                  <p className="text-gray-600">Sigara da dolaşımı bozar; aynı süre kaçınılmalıdır.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. Botoks sonrası spor yapılabilir mi?</h3>
                  <p className="text-gray-600">İlk 24 saat ağır spor yasak, hafif yürüyüş yapılabilir.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">6. Botoks sonrası makyaj yapılabilir mi?</h3>
                  <p className="text-gray-600">İlk 4-6 saat makyaj yapılmamalı, sonrasında yapılabilir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sonuç ve Klinik Etik Not
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks, mikron düzeyinde doz hassasiyeti olan bir uygulamadır.
                Alkol ise mikro dolaşımı etkileyen en belirgin faktördür.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Uygulama sonrası 24–48 saatlik basit bir bekleme, hem sonucun kalıcılığını hem de görsel doğallığı belirler.
                Bu kısa süreli özveri, haftalar süren mükemmel bir görünümle ödüllendirilir.
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-6">
                <p className="text-red-800 font-medium">
                  🩺 Dr. Barış Çin öneriyor: "Estetik uygulama bir yatırımdır. İlk 48 saat alkolden kaçınmak, yatırımın sigortasıdır."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>FDA (2024) – "Botulinum Toxin Post-Treatment Guidelines"</li>
                  <li>ISAPS Global Report (2024) – "Vascular Response After Neurotoxin Injection"</li>
                  <li>Harvard Health Publishing (2023) – "Alcohol and Microcirculation"</li>
                  <li>Türk Estetik Tıp Derneği (TETD) – "Botoks Sonrası Bakım Rehberi"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">#botoks</span>
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">#alkol</span>
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">#klinikbakım</span>
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">#ödem</span>
                <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">#asimetrikbotoks</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-red-600 to-orange-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Botoks Uygulaması ve Bakım Danışmanlığı</h3>
          <p className="mb-6">Uzman doktorumuzdan botoks uygulaması ve sonrası bakım hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Botoks uygulaması ve sonrası bakım hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-red-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/botoks-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Nedir ve Nasıl Yapılır?</h4>
              <p className="text-gray-600 text-sm">Botoksun ne olduğu, nasıl çalıştığı ve uygulama sürecinin ayrıntılı anlatımı.</p>
            </a>
            <a
              href="/tr/blog/botoks-fiyatlari-2026"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Fiyatları 2026</h4>
              <p className="text-gray-600 text-sm">Botoks fiyatlarını etkileyen faktörler ve 2026 güncel fiyat listesi.</p>
            </a>
            <a
              href="/tr/blog/botoks-etkisinin-suresi-ne-kadar-kalicidir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Etkisinin Süresi</h4>
              <p className="text-gray-600 text-sm">Botoksun ne kadar süre etkili olduğu ve kalıcılık faktörleri.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
